import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
  setIsLoadingRequestDetails,
  setIsLoadingRequestStatus,
  setIsLoadingUserOnboardingFormInitialData,
  setIsPodOnboardFormSubmited,
  setIsSubmitingPodOnboardForm,
  setIsSubmitingUserOnboardForm,
  setIsUserOnboarded,
  setPodList,
  setRequestDetails,
  setRequests,
  setRoles,
  setPaginationData,
  setIsLoadingRequests,
} from "./slice";
import {
  addUserToPod,
  getPods,
  getRequestDetails,
  getRequestList,
  getRequestListPaginated,
  getRoles,
  podOnboarding,
  PaginatedRequestsResponse,
} from "services/onboardingServices";
import {
  AddUserToPodFormData,
  OnboardingRequestItem,
  PodItem,
  PodOnboardingRequestBody,
  RequestDetails,
  RoleItem,
} from "utility/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { reactToastify, ToastType } from "utility/reactToastify";
import { RootState } from "api-store/store";
import { USER_ONBOARDING } from "utility/constants";
import { isRoleAdmin } from "pages/mlops/ducks/utils";

interface AddUserToPodResponse {
  sys_id: string;
  number: string;
  $$uiNotification: [];
  request_number: string;
  parent_id: string | null;
  request_id: string;
  parent_table: string;
  table: string;
}

function* submitUserOnboardForm(action: PayloadAction<AddUserToPodFormData>) {
  try {
    yield put(setIsSubmitingUserOnboardForm(true));
    // call user onboard API
    const response: { result: AddUserToPodResponse } = yield addUserToPod(
      action.payload
    );
    if (response?.result){
      yield put(setIsUserOnboarded(true));
      reactToastify({ type: ToastType.success, message: "Request raised", description: `${ action.payload.v_tdsp_user_would_you_like_to_add_or_remove_a_user === "add" ? USER_ONBOARDING.userOnboarding : USER_ONBOARDING.userOffBoarding } request has been successfully created.`, action: { link: `/track-requests/${response?.result?.request_number}`, title: "View details" }});
    }
  } catch (error) {
    console.info("Error", error);
    reactToastify({ type: ToastType.error, message: "User onboarding failed." });
  } finally {
    yield put(setIsSubmitingUserOnboardForm(false));
  }
}

function* loadUserOnboardingFormInitialData() {
  try {
    yield put(setIsLoadingUserOnboardingFormInitialData(true));
    // get pod list
    // yield delay(500);
    try {
      const podList: { result: Array<PodItem> } = yield call(getPods);
      if (podList) {
        yield put(setPodList(podList.result));
      }
    } catch (error: any) {
      console.info("Error fetching pods", error);
      reactToastify({ type: ToastType.error, message: "Failed to fetch pod names" });
    }
    // get roles
    try {
      const roles: { result: Array<RoleItem> } = yield call(getRoles);
      if (roles?.result) {
        yield put(setRoles(roles.result));
      }
    } catch (error: any) {
      console.info("Error fetching roles", error);
      reactToastify({ type: ToastType.error, message: "Failed to fetch user roles" });
    }
  } finally {
    yield put(setIsLoadingUserOnboardingFormInitialData(false));
  }
}

function* getRequests(action: PayloadAction<{ email: string }>) {
  try {
    const accessToken: string = yield select((rootState: RootState) => rootState.userData.access_token);
    const response: Array<OnboardingRequestItem> = yield call(
      getRequestList,
      action.payload.email,
      isRoleAdmin(accessToken) ? "admin" : "user"
    );
    yield put(setRequests(response));
  } catch (error) {
    console.info("Error", error);
  }
}

function* loadPodOnboardingFormInitialData() {
  try {
    const podList: { result: Array<PodItem> } = yield call(getPods);
    if (podList) {
      yield put(setPodList(podList.result));
    }
  } catch (error: any) {
    console.info("Error fetching pods", error);
    reactToastify({ type: ToastType.error, message: "Failed to fetch pod names" });
  }
  try {
    const roles: { result: Array<RoleItem> } = yield call(getRoles);
    if (roles?.result) {
      yield put(setRoles(roles.result));
    }
  } catch (error: any) {
    console.info("Error fetching roles", error);
    reactToastify({ type: ToastType.error, message: "Failed to fetch user roles" });
  }
}

function* callToGetRequestDetails(actions:PayloadAction<{ requestId: string, isRefreshStatusFlow?: boolean, isSilentRefresh?: boolean }> ) {
  try {
    if (actions.payload?.isRefreshStatusFlow) {
      yield put(setIsLoadingRequestStatus(true));
    } else if (!actions.payload?.isSilentRefresh) {
      yield put(setIsLoadingRequestDetails(true));
    }
    const response: RequestDetails = yield call(getRequestDetails, {requestId: actions.payload.requestId });
    yield put(setRequestDetails(response));
    console.info("response", response)
  } catch (error) {
    console.info("Error", error);
  } finally {
    yield put(setIsLoadingRequestDetails(false));
    yield put(setIsLoadingRequestStatus(false));
  }
}

function* submitPodOnboarding(action: PayloadAction<PodOnboardingRequestBody>) {
  try {
    yield put(setIsSubmitingPodOnboardForm(true));
    // call user onboard API
    const response: { result: AddUserToPodResponse } = yield podOnboarding(
      action.payload
    );
    if (response.result) {
      yield put(setIsPodOnboardFormSubmited(true));
      reactToastify({ type: ToastType.success, message: "Request raised", description: "Pod onboarding request has been successfully created.", action: { link: `/track-requests/${response?.result?.request_number}`, title: "View details" }});
    }
  } catch (error) {
    console.info("Error", error);
    reactToastify({ type: ToastType.error, message: "Pod onboarding failed." });
  } finally {
    yield put(setIsSubmitingPodOnboardForm(false));
  }
}

function* callToSubmitUserOnboardForm() {
  yield takeEvery(
    "onboarding/callToSubmitUserOnboardForm",
    submitUserOnboardForm
  );
}

function* callToLoadUserOnboardingFormInitialData() {
  yield takeEvery(
    "onboarding/callToLoadUserOnboardingFormInitialData",
    loadUserOnboardingFormInitialData
  );
}

function* callToGetRequests() {
  yield takeEvery("onboarding/callToGetRequests", getRequests);
}

function* getRequestsPaginated(action: PayloadAction<{ email: string }>) {
  try {
    yield put(setIsLoadingRequests(true));
    const accessToken: string = yield select((rootState: RootState) => rootState.userData.access_token);
    const filters: {
      status: string;
      onboardingType: string;
      requestNumber: string;
      raisedBy: string;
      raisedFor: string;
    } = yield select((rootState: RootState) => rootState.onboarding.filters);
    const sortBy: string = yield select((rootState: RootState) => rootState.onboarding.sortBy);
    const sortOrder: "asc" | "desc" = yield select((rootState: RootState) => rootState.onboarding.sortOrder);
    const currentPage: number = yield select((rootState: RootState) => rootState.onboarding.currentPage);
    const pageSize: number = yield select((rootState: RootState) => rootState.onboarding.pageSize);
    
    const response: PaginatedRequestsResponse = yield call(
      getRequestListPaginated,
      {
        email: action.payload.email,
        role: isRoleAdmin(accessToken) ? "admin" : "user",
        page: currentPage,
        page_size: pageSize,
        request_number: filters.requestNumber || undefined,
        raised_by: filters.raisedBy || undefined,
        raised_for: filters.raisedFor || undefined,
        status: filters.status || undefined,
        onboarding_type: filters.onboardingType || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      }
    );
    
    yield put(setRequests(response.user_details));
    yield put(setPaginationData({
      total_count: response.total_count,
      page: response.page,
      page_size: response.page_size,
      total_pages: response.total_pages,
      has_next: response.has_next,
      has_previous: response.has_previous,
    }));
  } catch (error) {
    console.info("Error", error);
    reactToastify({ type: ToastType.error, message: "Failed to fetch requests" });
  } finally {
    yield put(setIsLoadingRequests(false));
  }
}

function* callToGetRequestsPaginated() {
  yield takeLatest("onboarding/callToGetRequestsPaginated", getRequestsPaginated);
}

function* callToGetUserRoles() {
  yield takeEvery(
    "onboarding/callToLoadPodOnboardingFormInitialData",
    loadPodOnboardingFormInitialData
  );
}

function* callToGetRequestDetailsWatcher() {
  yield takeEvery(
    "onboarding/callToGetRequestDetails",
    callToGetRequestDetails
  );
}

function* callToSubmitPodOnboarding() {
  yield takeEvery(
    "onboarding/callToSubmitPodOnboarding",
    submitPodOnboarding
  );
}

export {
  callToSubmitUserOnboardForm,
  callToLoadUserOnboardingFormInitialData,
  callToGetRequests,
  callToGetRequestsPaginated,
  callToGetUserRoles,
  callToGetRequestDetailsWatcher,
  callToSubmitPodOnboarding,
};
